import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useSidebarNavItems } from "@/components/app-sidebar";
import { useAuth } from "@/contexts/auth-context";
import { useMyCompany, useMyPermissions } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { ChevronRight, LogOut, Moon, UserRound } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AvatarEditor } from "@/components/user-avatar-editor";
import { MeuPerfilScreen } from "@/components/mobile-app";
import { applyAgendaTheme, readAgendaThemeIsDark } from "@/lib/agenda-theme";

// Funcionário no celular/app (pedido 2026-10-09): em vez do app do
// cliente (Início/Mapa/Veículos/Eventos), só os menus de trabalho dele —
// Agenda, Usuários e Vendas — e só os que a sidebar do sistema já libera
// pra ele (useSidebarNavItems, mesma regra de permissão, nunca uma lista
// paralela). Cada aba abre a tela real da rota.
const FUNCIONARIO_MOBILE_URLS = ["/agenda", "/users", "/vendas"];

export function FuncionarioMobileShell({ children }: { children: ReactNode }) {
  const { logout, user, gpsDriveUser } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const { data: permissions } = useMyPermissions();
  const { data: myCompany } = useMyCompany();
  const navItems = useSidebarNavItems().filter((item) => FUNCIONARIO_MOBILE_URLS.includes(item.url));
  const [loggingOut, setLoggingOut] = useState(false);
  // Aba Perfil (pedido 2026-10-09: no lugar do "Sair" solto) — fica por
  // cima da tela atual, sem trocar de rota.
  const [profileOpen, setProfileOpen] = useState(false);
  const [meuPerfilOpen, setMeuPerfilOpen] = useState(false);
  const [dark, setDark] = useState(readAgendaThemeIsDark);
  // Relê a cada troca de tela: o menu lateral da Agenda também troca a
  // mesma preferência.
  useEffect(() => {
    const stored = readAgendaThemeIsDark();
    setDark(stored);
    applyAgendaTheme(stored);
  }, [pathname, profileOpen]);
  const toggleDark = (next: boolean) => {
    setDark(next);
    applyAgendaTheme(next);
  };

  const isActive = (url: string) => pathname === url || pathname.startsWith(`${url}/`);
  const currentAllowed = navItems.some((item) => isActive(item.url));
  const ready = !!permissions && !!myCompany;

  // Login sempre abre o Início — manda pro 1º menu liberado. Só decide
  // depois das permissões carregarem, pra não pular de tela no boot.
  useEffect(() => {
    if (!ready || currentAllowed) return;
    const target = navItems[0];
    if (target) navigate({ to: target.url, replace: true });
  }, [ready, currentAllowed, navItems, navigate]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate({ to: "/login", replace: true });
    }
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-background text-foreground">
      <main
        className="min-h-0 flex-1 overflow-auto"
        style={{ paddingBottom: "calc(4.5rem + env(safe-area-inset-bottom))" }}
      >
        {profileOpen ? (
          <div className="px-4 pb-6 pt-8">
            <div className="flex flex-col items-center">
              <AvatarEditor traccarUserId={user?.id} />
              <h2 className="mt-3 text-lg font-bold">{gpsDriveUser?.name ?? user?.name ?? ""}</h2>
              <p className="text-sm text-muted-foreground">{user?.email ?? ""}</p>
            </div>
            <div className="mt-6 space-y-2">
              <button
                type="button"
                onClick={() => setMeuPerfilOpen(true)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium"
              >
                <UserRound className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 text-left">Meu perfil</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
              <label className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium">
                <Moon className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1">Tema escuro</span>
                <Switch checked={dark} onCheckedChange={toggleDark} />
              </label>
              <button
                type="button"
                onClick={() => void handleLogout()}
                disabled={loggingOut}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-destructive disabled:opacity-60"
              >
                <LogOut className="h-5 w-5" />
                <span className="flex-1 text-left">{loggingOut ? "Saindo…" : "Sair"}</span>
              </button>
            </div>
          </div>
        ) : currentAllowed ? (
          children
        ) : ready && navItems.length === 0 ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div>
              <h1 className="text-lg font-semibold">Sem menus liberados</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Peça ao administrador para liberar Agenda, Usuários ou Vendas para o seu usuário.
              </p>
            </div>
          </div>
        ) : null}
      </main>
      <nav
        className="fixed inset-x-3 z-20 flex items-center justify-around rounded-2xl border border-slate-800 bg-slate-900/95 px-1 py-1 shadow-2xl backdrop-blur"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        {navItems.map((item) => (
          <TabButton
            key={item.url}
            emoji={item.emoji}
            label={item.title}
            active={!profileOpen && isActive(item.url)}
            onClick={() => {
              setProfileOpen(false);
              navigate({ to: item.url });
            }}
          />
        ))}
        <TabButton emoji="🙂" label="Perfil" active={profileOpen} onClick={() => setProfileOpen(true)} />
      </nav>
      {meuPerfilOpen && (
        <div className="fixed inset-0 z-50">
          <MeuPerfilScreen onClose={() => setMeuPerfilOpen(false)} />
        </div>
      )}
    </div>
  );
}

function TabButton({
  emoji,
  label,
  active,
  onClick,
}: {
  emoji: string;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 text-[11px] font-medium transition-colors",
        active ? "bg-white/10 text-white" : "text-slate-400",
      )}
    >
      <span className="text-lg leading-none">{emoji}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
